import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import MNTLogo from "../../Assets/Images/mnt-podsused-logo.png";
import MNTPodsusedLogo from "../../Assets/Images/mnt-logo.png";
import socerPlayerIcon from "../../Assets/Icons/soccer-player.png";
import championshipIcon from "../../Assets/Icons/championship.png";
import MNTTitleLogo from "../../Assets/Images/mnt-podsused.png";

const Navigation = ({
  teams = [],
  players = [],
  matches = [], // Ovdje stižu strijelci iz HomePage-a
  setActiveTab,
  setSelectedGroup,
}) => {
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Pomoćna funkcija za normalizaciju (izvan render petlje)
  const normalize = (str) => {
    if (!str) return "";
    return str
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  // Optimizirana pretraga
  const getFilteredResults = useCallback(() => {
    if (query.length < 2) return [];

    const q = normalize(query);
    let results = [];

    // 1. Fiksni linkovi
    results.push(
      ...[
        { name: "Završnica", type: "page", path: "/zavrsnica" },
        { name: "Strijelci", type: "page", path: "/strijelci" },
      ].filter((i) => normalize(i.name).includes(q)),
    );

    // 2. Grupe
    results.push(
      ...["A", "B", "C", "D"]
        .map((g) => ({ name: `Grupa ${g}`, type: "group", group: g }))
        .filter((i) => normalize(i.name).includes(q)),
    );

    // 3. Timovi
    results.push(
      ...teams
        .filter((t) => normalize(t.name).includes(q))
        .map((t) => {
          // LOG: Provjeri što se događa u konzoli
          const teamMatches = matches.filter((m) => {
            const nameA = m.team_a?.name || "";
            const nameB = m.team_b?.name || "";
            return (
              normalize(nameA) === normalize(t.name) ||
              normalize(nameB) === normalize(t.name)
            );
          });

          const options = [
            {
              name: `${t.name} (Grupa ${t.group_name})`,
              type: "group",
              group: t.group_name,
            },
          ];

          if (teamMatches.length > 0) {
            // Provjeri ima li ne-završenih
            const hasUpcoming = teamMatches.some((m) => !m.is_completed);

            if (hasUpcoming) {
              options.push({
                name: `${t.name} • Raspored`,
                type: "schedule",
                teamName: t.name,
              });
            } else {
              options.push({
                name: `${t.name} • Rezultati`,
                type: "results",
                teamName: t.name,
              });
            }
          }

          return options;
        })
        .flat(),
    );

    // 4. Igrači
    let foundPlayers = [];
    teams.forEach((team) => {
      let igraci = team.players;
      if (typeof igraci === "string") {
        try {
          igraci = JSON.parse(igraci);
        } catch (e) {
          igraci = [];
        }
      }

      if (Array.isArray(igraci)) {
        igraci.forEach((p) => {
          const playerName = p.name || p.ime || p.full_name || p.Ime || "";

          if (playerName && normalize(playerName).includes(q)) {
            const scorerInfo = players.find(
              (s) => normalize(s.name) === normalize(playerName),
            );

            foundPlayers.push({
              name: playerName,
              label: "Zadnja utakmica",
              type: "player",
              teamName: team.name,
            });

            if (scorerInfo) {
              foundPlayers.push({
                name: playerName,
                label: "Strijelci (Ukupno)",
                val: scorerInfo.goals,
                type: "page",
                path: "/strijelci",
              });
            }
          }
        });
      }
    });

    return [...results, ...foundPlayers].slice(0, 6);
  }, [query, teams, players, matches]); // Dodaj matches u dependency niz

  // Memoizirani rezultati
  const results = useMemo(() => getFilteredResults(), [getFilteredResults]);

  const handleSelect = (item) => {
    setIsSearchOpen(false);
    setQuery("");

    if (item.type === "page") {
      navigate(item.path);
    } else if (item.type === "group") {
      navigate("/");
      setActiveTab("groups");
      window.dispatchEvent(new Event("urlChanged"));
      setSelectedGroup(item.group);
    } else if (item.type === "schedule" || item.type === "results") {
      const tab = item.type === "schedule" ? "schedule" : "results";

      // Koristi "replace" ili "push" ali dodaj state ili dispatch event
      navigate(`/?tab=${tab}&highlight=${encodeURIComponent(item.teamName)}`);
      setActiveTab(tab);

      // OVO JE KLJUČNO: Okinemo custom event da HomeRaspored čuje da smo kliknuli
      window.dispatchEvent(new Event("urlChanged"));
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const searchRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
        setQuery("");
      }
    };
    const handleClickOutside = (event) => {
      // Ako kliknemo izvan searchRef-a, zatvori pretragu
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setQuery("");
      }
    };

    if (isSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchOpen]);

  return (
    <header className="header">
      <div className={`full-menu ${isMenuOpen ? "open" : ""}`}>
        <ul className="menu-links">
          <img src={MNTPodsusedLogo} alt="MNT Podsused" />
          <li onClick={() => handleNavClick("/")}>Naslovna</li>
          <li onClick={() => handleNavClick("/prvaci-turnira")}>Arhiva</li>
          {/* <li onClick={() => handleNavClick("/pravila-turnira")}>
            Pravila turnira
          </li> */}
          <li onClick={() => handleNavClick("/bela-turnir")}>Bela turnir</li>
          {/*  <li onClick={() => handleNavClick("/kako-do-nas")}>Kako do nas?</li> */}
          <li onClick={() => handleNavClick("/nagrade")}>Nagrade</li>
          <li onClick={() => handleNavClick("/sponzori")}>Sponzori</li>
          <li onClick={() => handleNavClick("/dogadaji")}>Ostali događaji</li>
          <div className="social-medias">
            <a
              href="https://www.instagram.com/mntpodsused/"
              target="_blank"
              rel="noreferrer"
            >
              <i className="fi fi-brands-instagram"></i>
            </a>
            <a
              href="https://www.facebook.com/people/MNT-Podsused/61591314481188/"
              target="_blank"
              rel="noreferrer"
            >
              <i className="fi fi-brands-facebook"></i>
            </a>
          </div>
        </ul>
      </div>

      <nav className="navbar" ref={searchRef}>
        <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <img className="logo" src={MNTLogo} alt="Logo" />
        </div>

        <div
          className={`center-section ${isSearchOpen ? "search-active" : ""}`}
        >
          {!isSearchOpen ? (
            <img src={MNTTitleLogo} className="img-title" alt="MNT Podsused" />
          ) : (
            <div className="search-wrapper">
              <input
                type="text"
                placeholder="Traži..."
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="actions">
          <button
            className="search-btn"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
          >
            <i
              className={
                isSearchOpen ? "fi fi-rs-cross-small" : "fi fi-rs-search"
              }
            ></i>
          </button>
          <div
            className={isMenuOpen ? "hamburger active" : "hamburger"}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </div>
        </div>

        {isSearchOpen && query.length >= 2 && (
          <div className="search-results">
            {results.length > 0 ? (
              results.map((res, i) => {
                // Funkcija za odabir ikone (sada prima res!)
                const renderIcon = (item) => {
                  if (!item) return <i className="fi fi-rs-search"></i>;

                  if (item.type === "page") {
                    if (item.name === "Završnica")
                      return (
                        <img
                          src={championshipIcon}
                          alt="Završnica"
                          className="icon-img"
                        />
                      );
                    if (item.name === "Strijelci" || item.path === "/strijelci")
                      return (
                        <img
                          src={socerPlayerIcon}
                          alt="Strijelci"
                          className="icon-img"
                        />
                      );
                  }
                  if (item.type === "player")
                    return <i className="fi fi-br-football"></i>;
                  if (item.type === "scorer")
                    return <i className="fi fi-br-trophy"></i>;
                  if (item.type === "team")
                    return <i className="fi fi-rs-shield"></i>;
                  if (item.type === "group")
                    return <i className="fi fi-rs-shield"></i>;
                  return <i className="fi fi-rs-search"></i>;
                };

                return (
                  <div
                    key={i}
                    className={`result-item ${res.type === "team" ? "team-results" : ""}`}
                    onClick={() => handleSelect(res)}
                  >
                    <div className="res-icon">{renderIcon(res)}</div>{" "}
                    {/* Proslijeđeno res! */}
                    <div className="res-info">
                      <span className="res-name">{res.name}</span>
                      <span className="res-sub">
                        {res.type === "player" ? (
                          res.label // Ispisuje samo "Zadnja utakmica"
                        ) : res.type === "page" && res.val !== undefined ? (
                          // Ovdje ostaje stari prikaz za Strijelce (Ukupno)
                          <>
                            {res.label} • {res.val}
                            <i
                              className="fi fi-rs-football"
                              style={{ marginLeft: "5px", fontSize: "0.7rem" }}
                            ></i>
                          </>
                        ) : (
                          res.sub // Za ostale (timovi, grupe)
                        )}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-results">Nema rezultata za "{query}"</div>
            )}
          </div>
        )}
      </nav>
      <a
        href="https://maps.app.goo.gl/nj6Uz6TZxV2TCk4y5"
        target="_blank"
        rel="noreferrer"
        className="location-bar"
      >
        <i className="fi fi-rs-marker"></i>
        <span>OŠ bana Josipa Jelačića, Podsused</span>
      </a>
    </header>
  );
};

export default Navigation;
